import { useCallback, useEffect, useState } from "react";
import { Loader2, Star, MessageSquareHeart, Feather, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

function Stars({ value, onChange, size = "w-6 h-6", interactive = false }) {
  return (
    <div className="flex gap-0.5" dir="ltr">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange(n)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          data-testid={interactive ? `star-${n}` : undefined}
          aria-label={`${n} stars`}
        >
          <Star
            className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-espresso/25"} ${interactive ? "hover:scale-110 transition-transform" : ""}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySaving, setReplySaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data.reviews || []);
      const mine = (data.reviews || []).find((r) => r.mine);
      if (mine) {
        setRating(mine.rating);
        setMessage(mine.message);
      }
    } catch (_e) {
      void _e;
      setReviews([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) return toast.error(t("reviews.pick_stars"));
    setSending(true);
    try {
      await api.post("/reviews", { rating, message: message.trim() });
      toast.success(t("reviews.thanks"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const avg =
    reviews && reviews.length
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : null;

  const saveReply = async (id) => {
    setReplySaving(true);
    try {
      await api.post(`/reviews/${id}/reply`, { reply: replyText.trim() });
      toast.success(replyText.trim() ? t("reviews.reply_saved") : t("reviews.reply_deleted"));
      setReplyingId(null);
      setReplyText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setReplySaving(false);
    }
  };

  const deleteReply = async (id) => {
    try {
      await api.post(`/reviews/${id}/reply`, { reply: "" });
      toast.success(t("reviews.reply_deleted"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="reviews-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
          <MessageSquareHeart className="w-7 h-7 text-olive" />
          {t("reviews.title")}
        </h1>
        <p className="text-sm text-espresso/70 mt-1">{t("reviews.sub")}</p>
      </div>

      {/* My review form */}
      <div className="bg-white rounded-xl border border-app-border p-5 space-y-3" data-testid="review-form">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-olive font-bold block mb-1.5">
            {t("reviews.your_rating")}
          </label>
          <Stars value={rating} onChange={setRating} interactive size="w-8 h-8" />
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("reviews.placeholder")}
          rows={4}
          data-testid="review-textarea"
        />
        <Button
          onClick={submit}
          disabled={sending || !rating}
          className="bg-olive hover:bg-olive/90"
          data-testid="review-submit-button"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reviews.submit")}
        </Button>
      </div>

      {/* All reviews */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-olive">
            {t("reviews.all")} {reviews ? `(${reviews.length})` : ""}
          </h2>
          {avg && (
            <span className="flex items-center gap-1 text-sm font-bold text-espresso" data-testid="reviews-average">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {avg}
            </span>
          )}
        </div>
        {reviews === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
        ) : reviews.length === 0 ? (
          <div className="text-sm text-espresso/50" data-testid="reviews-empty">{t("reviews.none")}</div>
        ) : (
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.map((r, i) => (
              <div key={i} className={`bg-white rounded-xl border p-4 ${r.mine ? "border-olive/40" : "border-app-border"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-espresso">{r.user_name}</span>
                  {r.mine && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-olive/10 text-olive">
                      {t("reviews.you")}
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-espresso/40">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}
                  </span>
                </div>
                <div className="mt-1.5">
                  <Stars value={r.rating} size="w-4 h-4" />
                </div>
                {r.message && (
                  <p className="text-sm text-espresso/80 mt-2 whitespace-pre-wrap">{r.message}</p>
                )}

                {r.admin_reply && replyingId !== r.id && (
                  <div className="mt-3 ml-3 rtl:ml-0 rtl:mr-3 pl-3 rtl:pl-0 rtl:pr-3 border-l-2 rtl:border-l-0 rtl:border-r-2 border-olive/40 bg-olive/5 rounded-r-lg rtl:rounded-r-none rtl:rounded-l-lg p-2.5" data-testid={`admin-reply-${i}`}>
                    <div className="flex items-center gap-1.5 text-olive">
                      <Feather className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-bold uppercase tracking-wide">{t("reviews.team_badge")}</span>
                      {r.admin_reply_at && (
                        <span className="ml-auto rtl:ml-0 rtl:mr-auto text-[10px] text-espresso/40">
                          {new Date(r.admin_reply_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-espresso/80 mt-1 whitespace-pre-wrap">{r.admin_reply}</p>
                  </div>
                )}

                {isAdmin && replyingId === r.id && (
                  <div className="mt-3 space-y-2" data-testid={`reply-form-${i}`}>
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={t("reviews.reply_placeholder")}
                      rows={3}
                      data-testid="reply-textarea"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveReply(r.id)} disabled={replySaving} className="bg-olive hover:bg-olive/90" data-testid="reply-save-button">
                        {replySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reviews.reply_save")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setReplyingId(null); setReplyText(""); }} data-testid="reply-cancel-button">
                        {t("reviews.reply_cancel")}
                      </Button>
                    </div>
                  </div>
                )}

                {isAdmin && replyingId !== r.id && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-olive/40 text-olive hover:bg-olive/10"
                      onClick={() => { setReplyingId(r.id); setReplyText(r.admin_reply || ""); }}
                      data-testid={`reply-button-${i}`}
                    >
                      <Pencil className="w-3 h-3 mr-1 rtl:mr-0 rtl:ml-1" />
                      {r.admin_reply ? t("reviews.reply_edit") : t("reviews.reply")}
                    </Button>
                    {r.admin_reply && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => deleteReply(r.id)}
                        data-testid={`reply-delete-button-${i}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1 rtl:mr-0 rtl:ml-1" />
                        {t("reviews.reply_delete")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
